// app.roleAdmin — manage the app's role taxonomy from the client side.
// Reads the permissions manifest (.s3p/permissions.json), materializes its
// declarations into Cognito groups + IAM roles + Identity Pool
// rules, and supports reverse-lookup for the database explorer.
//
// IAM scope required:
//   - `iam:CreateRole`, `iam:PutRolePolicy`, `iam:GetRole`, `iam:TagRole`
//     on `arn:aws:iam::{accountId}:role/s3p-{slug}-*`
//   - `cognito-idp:CreateGroup`, `cognito-idp:GetGroup` on the
//     {slug} pool ARN
//   - `cognito-identity:GetIdentityPoolRoles` and `SetIdentityPoolRoles`
//     on the {slug} Identity Pool ARN
//   - `iam:PassRole` on `arn:aws:iam::{accountId}:role/s3p-{slug}-*`
//
// Held by `{slug}:admin` (via the `manage-roles` capability) and
// `{slug}:owner` baselines.
//
// The manifest shapes + shared renderers (grantsToPolicy, toSections,
// renderRolePolicy, …) live in policy-templates.ts, shared with the CLI's
// `s3p deploy --sync-roles` so the two materialization paths cannot drift.

import {
  GetObjectCommand,
  PutObjectCommand,
  type S3Client,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-s3@3.1054.0/+esm";

import { CognitoAdmin } from "./cognito-admin.ts";
import { IamAdmin } from "./iam-admin.ts";
import { IdentityPoolAdmin } from "./identity-pool-admin.ts";
import {
  type BaselineRoleScope,
  type RolesManifest,
  baselineCapabilities,
  baselineRoleNames,
  compilePermissionsMap,
  defaultRolesManifest,
  grantsToPolicy,
  visitorAnalyticsStatement,
  appRoleIamName,
  appRoleLocalName,
  appRolePolicies,
  identityPoolTrustPolicy,
  renderRoleExtensionPolicy,
  renderRolePolicy,
  renderRolePolicyWithCaps,
  renderUserPolicy,
  rolePermissionsOf,
  tagsFor,
  toSections,
} from "./policy-templates.ts";
import { paths } from "./paths.ts";

// The manifest shapes + shared renderers live in policy-templates.ts so the
// CLI's `s3p deploy --sync-roles` consumes the SAME code. Re-exported here so
// existing imports (SDK index, tests) keep working.
export {
  OPERATOR_ROLES,
  OPERATOR_ROLE_META,
  appRoleGroupName,
  appRoleIamName,
  appRoleLocalName,
  appRolePolicies,
  compilePermissionsMap,
  grantsToPolicy,
  resolveGrantEntry,
  rolePermissionsOf,
  toSections,
} from "./policy-templates.ts";
export type {
  AccessGrant,
  CompiledPermissionsMap,
  ManifestSections,
  OperatorRole,
  RoleDef,
  RoleGrants,
  RoleKind,
  RolePermissions,
  RolePolicy,
  RolesManifest,
} from "./policy-templates.ts";

export interface RolesModuleConfig {
  region: string;
  /** App slug. */
  slug: string;
  /** App's S3 bucket. */
  bucket: string;
  /** App's Cognito User Pool id. */
  userPoolId: string;
  /** App's Cognito User Pool SPA client id. */
  spaClientId: string;
  /** App's server App Client id (/admin + the console). Optional, but when
   *  present its tokens get the same role-mapping rules (Deny-resolved) —
   *  without it, owners signing in on the console fall back to the default
   *  authenticated role. */
  serverClientId?: string;
  /** App's Cognito Identity Pool id. */
  identityPoolId: string;
  /** Per-request credentials provider. */
  credentials: () => Promise<{
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken?: string;
  }>;
  /** Warmed S3 client for the app bucket — used by `save()` to persist the
   *  edited manifest back to the bucket. Optional: when absent, `save`
   *  throws (read-only consumers don't need it). */
  getClient?: () => Promise<S3Client>;
  /** S3 key the manifest is persisted at. Default `.s3p/permissions.json`
   *  — the same object `s3p deploy` publishes, so a later deploy
   *  deterministically overwrites runtime edits. */
  manifestKey?: string;
  /** Fallback for the identity-pool mapping step. Cognito ENHANCED-FLOW
   *  credentials (every browser session) carry an AWS session policy that
   *  excludes `cognito-identity` control-plane calls — Get/Set
   *  IdentityPoolRoles are AccessDenied no matter what the role allows. When
   *  the direct call is denied and this hook is present, sync routes the
   *  step through the platform's `sync-role-mappings` broker instead.
   *  Node/CLI callers (operator IAM creds) never need it. */
  mappingsBroker?: (args: {
    slug: string;
    rules: Array<{ groupName: string; roleArn: string }>;
    hasVisitor: boolean;
  }) => Promise<unknown>;
  /** Whether this project's OWN pool carries the owner/admin tier groups.
   *  TRUE only for the root project (its pool IS the platform pool, so
   *  operators and end-users coincide). FALSE for every federated child:
   *  end-users can never be console users there — tier power comes only
   *  from platform-pool `{slug}:operator-*` groups, so sync neither creates
   *  own-pool tier groups nor maps them. Default FALSE — an unconfigured
   *  caller heals toward the invariant, never against it. (The old default
   *  of true served pre-federation projects; that shape is retired.) */
  ownPoolTiers?: boolean;
}

export interface SyncResult {
  defaultApplied: boolean;
  ruleCount: number;
  rules: Array<{ groupName: string; roleArn: string }>;
}

export interface SyncOpts {
  /** Step announcements for UI progress (the Permissions panel's Apply
   *  bar). Called with a short human line as each phase starts. */
  onProgress?: (step: string) => void;
}

export interface RolesModule {
  /** Fetch the current declarative taxonomy from the bundled file. */
  list(): Promise<RolesManifest>;
  /** Persist an edited manifest back to .s3p/permissions.json in the bucket
   *  (the runtime overlay). Does NOT touch AWS — call `sync()` to apply.
   *  A subsequent `s3p deploy` overwrites this from the codebase file, so
   *  runtime edits should be pulled back into source to be made permanent.
   *  Throws if the module was created without a `getClient`. */
  save(manifest: RolesManifest): Promise<void>;
  /** Materialize the taxonomy as Cognito groups + IAM roles +
   *  Identity Pool mappings. Includes baseline rules so existing
   *  group→role bindings survive. Role-level work runs in PARALLEL
   *  (each role is an independent AWS resource); the mapping step is a
   *  barrier after them (it references the role ARNs). */
  sync(manifest?: RolesManifest, opts?: SyncOpts): Promise<SyncResult>;
  /** Reverse-lookup: given an S3 path, which roles can read/write it?
   *  Reads each role's inline policy and matches the path against the
   *  Resource ARN patterns. Useful for the database explorer. */
  whoCan(path: string): Promise<{ readers: string[]; writers: string[] }>;
}

export function createRolesModule(cfg: RolesModuleConfig): RolesModule {
  const accountId = (() => {
    // The bucket name doubles as a fallback derivation path; but we
    // need the accountId for ARNs. Pull from the bucket name format
    // `s3p-{accountId}-{slug}` rather than another config field.
    const m = cfg.bucket.match(/^s3p-(\d{12})-/);
    if (m?.[1]) return m[1];
    throw new Error(`createRolesModule: cannot parse accountId from bucket '${cfg.bucket}'`);
  })();

  const cognito = new CognitoAdmin({ region: cfg.region, credentials: cfg.credentials });
  const iam = new IamAdmin({ region: cfg.region, credentials: cfg.credentials });
  const idp = new IdentityPoolAdmin({ region: cfg.region, credentials: cfg.credentials });

  // One provider key per app client (spa + server). The console signs in via
  // the server client, which needs its own mapping or owners get the default
  // role. The two kinds resolve ambiguity differently (spa → member fallback,
  // server → Deny), so they're kept apart — mirroring provisioning + the CLI.
  const providerKey = (c: string) =>
    `cognito-idp.${cfg.region}.amazonaws.com/${cfg.userPoolId}:${c}`;
  const spaProviderKeys = cfg.spaClientId ? [providerKey(cfg.spaClientId)] : [];
  const serverProviderKeys = cfg.serverClientId ? [providerKey(cfg.serverClientId)] : [];

  const scope: BaselineRoleScope = {
    slug: cfg.slug,
    bucket: cfg.bucket,
    region: cfg.region,
    accountId,
    userPoolId: cfg.userPoolId,
    identityPoolId: cfg.identityPoolId,
  };

  const manifestKey = cfg.manifestKey ?? paths().permissionsManifest();
  const ownPoolTiers = cfg.ownPoolTiers ?? false;

  /** Reject a body that's actually the SPA's index.html fallback (returned by
   *  the host for a missing key) rather than letting JSON.parse blow up. */
  function parseManifest(text: string): RolesManifest {
    if (text.trimStart().startsWith("<")) throw new Error("not JSON (SPA fallback)");
    return JSON.parse(text) as RolesManifest;
  }

  async function fetchManifest(): Promise<RolesManifest> {
    // The manifest is PRIVATE config (not a runtime-data artifact — the app
    // reads roles from the JWT, not this file). Only the console Roles panel +
    // sync read it, both authenticated — so this is an authenticated read of
    // the bucket copy, never a public web fetch (the manifest is never
    // published under public/).
    if (cfg.getClient) {
      try {
        const s3 = await cfg.getClient();
        const out = await s3.send(new GetObjectCommand({ Bucket: cfg.bucket, Key: manifestKey }));
        const body = await (
          out.Body as { transformToString: () => Promise<string> } | undefined
        )?.transformToString();
        if (body) return parseManifest(body);
      } catch {
        // fall through (key may not exist yet, or no read perms)
      }
    }
    // Nothing materialized yet — return the canonical baseline taxonomy so
    // the panel still renders (visitor / member / admin / owner).
    return defaultRolesManifest(cfg.slug) as RolesManifest;
  }

  return {
    list: fetchManifest,

    async save(manifest: RolesManifest) {
      if (!cfg.getClient) {
        throw new Error("app.roles.save: module created without getClient — cannot persist");
      }
      const s3 = await cfg.getClient();
      await s3.send(
        new PutObjectCommand({
          Bucket: cfg.bucket,
          Key: manifestKey,
          Body: `${JSON.stringify(manifest, null, 2)}\n`,
          ContentType: "application/json; charset=utf-8",
          CacheControl: "no-cache",
        }),
      );
    },

    async sync(manifest?: RolesManifest, opts?: SyncOpts) {
      const progress = opts?.onProgress ?? (() => {});
      // Normalize the flat manifest into the sectioned view the materializer
      // works on. Each role's sectioned `grants` compile to path globs via
      // `grantsToPolicy` at the point of rendering.
      const base = manifest ?? (await fetchManifest());
      const s = toSections(cfg.slug, base);

      // 1. THE MEMBER FLOOR — re-rendered from the canonical template, always
      //    (mirrors the CLI). This is the policy every signed-in user gets;
      //    it's derived, not authored, and leaving it stale meant a
      //    panel-only project could never receive a renderUserPolicy fix
      //    (e.g. layout 2 moving private data to `s3db/private/{sub}/`).
      //    A member `permissions.functions` declaration NARROWS the floor's
      //    invoke-all to exactly those functions.
      const member = s.member;
      const memberPerms = member ? rolePermissionsOf("member", member) : {};
      const memberApplied = !!(
        member?.grants ||
        member?.permissions ||
        member?.capabilities?.length
      );
      // Every role below is an INDEPENDENT AWS resource — render them all
      // concurrently and barrier before the mapping step (which references
      // the role ARNs). Same requests, a fraction of the wall-clock.
      const floorTask = async () => {
        await iam.putInlinePolicy({
          roleName: `s3p-${cfg.slug}-member`,
          policyName: `s3p-${cfg.slug}-member-s3`,
          policyDocument: renderUserPolicy(cfg.bucket, {
            ...(memberPerms.functions ? { memberFunctions: memberPerms.functions } : {}),
          }),
        });
        // 1b. `member` extension (formerly `default`) layered on the floor:
        //     only the app's declared globs + any capabilities.
        if (memberApplied) {
          await iam.putInlinePolicy({
            roleName: `s3p-${cfg.slug}-member`,
            policyName: `${cfg.slug}-app-default`,
            policyDocument: renderRoleExtensionPolicy(
              scope,
              grantsToPolicy(memberPerms),
              member?.capabilities ?? [],
            ),
          });
        }
      };

      // 2. Baselines. Group is `{slug}:{name}` (local, token-facing); IAM role
      //    is `s3p-{slug}-{name}`. These DO get the member floor, since they're
      //    standalone supersets of member.
      //
      //    `owner` is LOCKED, which means its capabilities come from the
      //    canonical template — NOT from the manifest, which an admin can edit.
      //    Locked does NOT mean "never materialized": it used to, and that was a
      //    silent upgrade hole. Every new baseline capability (e.g. `observe`)
      //    reached admin on the next sync but never reached OWNER, who then had
      //    strictly LESS power than their own admins until someone re-provisioned
      //    the project from scratch. Sync now rewrites owner from
      //    baselineCapabilities("owner") — editable by nobody, upgradable by
      //    everybody.
      //
      //    `toSections` force-includes canonical admin/owner entries when the
      //    manifest omits them, so a project that never restates its baselines
      //    still heals on every sync (a manifest entry wins).
      const baselineTasks = Object.entries(s.baselines).map(([name, def]) => async () => {
        const locked = name === "owner" || def.locked;
        const caps = locked ? baselineCapabilities("owner") : (def.capabilities ?? []);
        const policy = locked ? undefined : grantsToPolicy(rolePermissionsOf(name, def));
        const group = `${cfg.slug}:${name}`;
        const roleName = `s3p-${cfg.slug}-${name}`;
        // The tier IAM role + policy always heal (operator federation maps
        // platform groups onto these ARNs) — but the OWN-POOL tier group
        // exists only on the root, where end-users legitimately hold tiers.
        if (ownPoolTiers) await cognito.ensureGroup(cfg.userPoolId, group);
        await iam.ensureRole({
          name: roleName,
          description: def.description ?? `s3p ${cfg.slug} baseline role '${group}'`,
          trustPolicy: identityPoolTrustPolicy({
            identityPoolId: cfg.identityPoolId,
            authenticated: true,
          }),
          tags: tagsFor(cfg.slug, "identity-pool"),
        });
        await iam.putInlinePolicy({
          roleName,
          policyName: `${roleName}-policy`,
          policyDocument: renderRolePolicyWithCaps(scope, policy, caps),
        });
      });

      // 3. Per app-role: Cognito group + IAM role + IDP rule. App roles live
      //    in their own namespace — `{slug}:role:{name}` / `s3p-{slug}-role-
      //    {name}` — so no manifest name can collide with a tier or component.
      const appRoleTasks = Object.entries(s.roles).map(([roleName, def]) => async () => {
        await cognito.ensureGroup(cfg.userPoolId, roleName);
        const local = appRoleLocalName(cfg.slug, roleName) ?? roleName;
        const safeName = appRoleIamName(cfg.slug, local);
        const arn = await iam.ensureRole({
          name: safeName,
          description: def.description ?? `s3p ${cfg.slug} role '${roleName}'`,
          trustPolicy: identityPoolTrustPolicy({
            identityPoolId: cfg.identityPoolId,
            authenticated: true,
          }),
          tags: tagsFor(cfg.slug, "identity-pool"),
        });
        // Floor + extension — see `appRolePolicies`. An app role is a SUPERSET
        // of member, never a replacement for it (one role per user).
        const { floor, extension } = appRolePolicies({
          scope,
          memberPermissions: memberPerms,
          memberCapabilities: member?.capabilities,
          permissions: rolePermissionsOf(roleName, def),
        });
        await iam.putInlinePolicy({
          roleName: safeName,
          policyName: `${safeName}-floor`,
          policyDocument: floor,
        });
        await iam.putInlinePolicy({
          roleName: safeName,
          policyName: `${safeName}-s3`,
          policyDocument: extension,
        });
        return { groupName: roleName, roleArn: arn };
      });

      // 3b. Visitor — the Identity Pool's UNAUTHENTICATED role. Not a group;
      //     materialized as `{slug}-visitor` (unauthenticated trust) and set as
      //     the pool's `unauthenticated` role below. No policy = no access.
      const visitorTask = async (): Promise<string | undefined> => {
        if (!s.visitor) return undefined;
        const arn = await iam.ensureRole({
          name: `s3p-${cfg.slug}-visitor`,
          description: s.visitor.description ?? `s3p ${cfg.slug} visitor (unauthenticated)`,
          trustPolicy: identityPoolTrustPolicy({
            identityPoolId: cfg.identityPoolId,
            authenticated: false,
          }),
          tags: tagsFor(cfg.slug, "identity-pool"),
        });
        // Manifest grants (if any) + the baked-in anonymous-telemetry write.
        // Storage only: visitor has no JWT, so functions/custom don't apply.
        const visitorPolicy = grantsToPolicy(rolePermissionsOf("visitor", s.visitor));
        const doc = renderRolePolicy(cfg.bucket, visitorPolicy) as { Statement: unknown[] };
        doc.Statement.push(visitorAnalyticsStatement(cfg.bucket));
        await iam.putInlinePolicy({
          roleName: `s3p-${cfg.slug}-visitor`,
          policyName: `s3p-${cfg.slug}-visitor-s3`,
          policyDocument: doc,
        });
        return arn;
      };

      const roleCount = 1 + baselineTasks.length + appRoleTasks.length + (s.visitor ? 1 : 0);
      progress(`Rendering ${roleCount} role(s) — groups, trust, policies…`);
      const [, appRules, visitorRoleArn] = await Promise.all([
        floorTask(),
        Promise.all(appRoleTasks.map((t) => t())),
        visitorTask(),
        ...baselineTasks.map((t) => t()),
      ]);

      // 4. Identity Pool mapping = own-pool baseline rules (owner + admin —
      //    only where end-users hold tiers) + app rules, plus the
      //    unauthenticated (visitor) role. Federated children map NO own-pool
      //    tier groups: end-users cannot be console users there.
      const baselineRules = ownPoolTiers
        ? baselineRoleNames(cfg.slug).map((spec) => ({
            groupName: spec.group,
            roleArn: `arn:aws:iam::${accountId}:role/${spec.roleName}`,
          }))
        : [];
      const allRules = [...baselineRules, ...appRules];

      // Browser (enhanced-flow) credentials can NEVER call cognito-identity
      // (AWS scope-down session policy) — when the broker transport exists,
      // go straight to it instead of paying two always-denied requests.
      // Node/CLI callers (no broker configured) keep the direct call.
      const mapTask = async () => {
        if (cfg.mappingsBroker) {
          await cfg.mappingsBroker({
            slug: cfg.slug,
            rules: allRules,
            hasVisitor: Boolean(visitorRoleArn),
          });
          return;
        }
        await idp.putRoleMappingRules({
          identityPoolId: cfg.identityPoolId,
          spaProviderKeys,
          serverProviderKeys,
          authenticatedRoleArn: `arn:aws:iam::${accountId}:role/s3p-${cfg.slug}-member`,
          unauthenticatedRoleArn: visitorRoleArn,
          rules: allRules,
        });
      };

      // 5. The compiled permissions map — the member-readable derivative
      //    `app.me.can()` / the runtime's `ctx.caller` resolve against
      //    (custom ids + function names, never storage globs). Best-effort:
      //    a read-only module (no getClient) skips it, and the CLI path
      //    writes its own copy. Independent of the mapping step → parallel.
      const compiledTask = async () => {
        if (!cfg.getClient) return;
        const s3 = await cfg.getClient();
        await s3.send(
          new PutObjectCommand({
            Bucket: cfg.bucket,
            Key: paths().permissionsCompiled(),
            Body: `${JSON.stringify(compilePermissionsMap(cfg.slug, base), null, 2)}\n`,
            ContentType: "application/json; charset=utf-8",
            CacheControl: "no-cache",
          }),
        );
      };

      progress(
        cfg.mappingsBroker
          ? "Wiring Identity Pool mappings (via the platform broker)…"
          : "Wiring Identity Pool mappings…",
      );
      await Promise.all([mapTask(), compiledTask()]);

      return {
        defaultApplied: memberApplied,
        ruleCount: allRules.length,
        rules: allRules,
      };
    },

    async whoCan(path: string) {
      // The same view sync materializes — each role's grants compiled to globs.
      const s = toSections(cfg.slug, await fetchManifest());
      const readers: string[] = [];
      const writers: string[] = [];

      // Always check the baseline + default policy as `:member` — the same
      // floor renderUserPolicy grants.
      if (matchesAny(path, ["s3db/shared/*", "logs/*", "files/shared/*"])) {
        readers.push(`${cfg.slug}:member`);
      }
      if (
        matchesAny(path, ["s3db/shared/users/*", "s3db/private/*", "files/private/*", "logs/*"]) &&
        path.includes(":sub:")
      ) {
        // Conservative: report `:member` for any own-prefixed path.
        writers.push(`${cfg.slug}:member (own only)`);
      }
      const memberPolicy = grantsToPolicy(
        s.member ? rolePermissionsOf("member", s.member) : undefined,
      );
      if (matchesAny(path, memberPolicy.read ?? []))
        readers.push(`${cfg.slug}:member (default)`);
      if (matchesAny(path, memberPolicy.write ?? []))
        writers.push(`${cfg.slug}:member (default)`);
      if (matchesAny(path, memberPolicy.create ?? []))
        writers.push(`${cfg.slug}:member (create-only)`);

      // Each named role.
      for (const [name, def] of Object.entries(s.roles)) {
        const p = grantsToPolicy(rolePermissionsOf(name, def));
        if (matchesAny(path, p.read ?? [])) readers.push(name);
        if (matchesAny(path, p.write ?? [])) writers.push(name);
        if (matchesAny(path, p.create ?? [])) writers.push(`${name} (create-only)`);
      }

      // Baseline admins inherit member rights; explicitly list them.
      readers.push(`${cfg.slug}:owner`, `${cfg.slug}:admin`);
      writers.push(`${cfg.slug}:owner`);

      return { readers: dedup(readers), writers: dedup(writers) };
    },
  };
}

function matchesAny(path: string, patterns: string[]): boolean {
  return patterns.some((p) => globMatch(path, p));
}

function globMatch(path: string, pattern: string): boolean {
  // Treat * as ".*" and ${jwt:sub} / ${aws:userid} as wildcards too.
  const normalized = pattern.replace(/\$\{[^}]+\}/g, "*");
  const re = new RegExp(
    `^${normalized.replace(/[.+?^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*")}$`,
  );
  return re.test(path);
}

function dedup(arr: string[]): string[] {
  return Array.from(new Set(arr));
}
